/**
 * TopicsList Component
 *
 * Grid container for TopicCard components.
 * Handles loading states, empty states, and optimistic UI updates for deletions.
 */

import { useMemo, useState, useOptimistic, useCallback, useTransition } from 'react';
import { Sparkles, Plus } from 'lucide-react';
import { ITopicsListProps, ITopic } from '../../@types/topics';
import { TopicCard } from './TopicCard';
import { ManualTopicDialog } from './ManualTopicDialog';
import { Button } from '../ui/button';
import { Id } from '../../../convex/_generated/dataModel';

const TopicsList = ({ topics, onDelete, onGenerate, isLoading }: ITopicsListProps) => {
  const [deletingTopicId, setDeletingTopicId] = useState<Id<'topics'> | null>(null);
  const [isManualTopicDialogOpen, setIsManualTopicDialogOpen] = useState(false);
  const [editingTopic, setEditingTopic] = useState<ITopic | undefined>(undefined);
  const [, startTransition] = useTransition();

  // Optimistic UI: Remove topic immediately while deletion processes
  const [optimisticTopics, removeOptimisticTopic] = useOptimistic(
    topics,
    (currentTopics: ITopic[], topicIdToRemove: Id<'topics'>) => {
      return currentTopics.filter(topic => topic._id !== topicIdToRemove);
    }
  );

  const handleDelete = useCallback(
    async (topicId: Id<'topics'>) => {
      setDeletingTopicId(topicId);

      // Optimistically remove from UI (wrapped in transition for React 19)
      startTransition(() => {
        removeOptimisticTopic(topicId);
      });

      try {
        // Perform actual deletion
        await onDelete(topicId);
      } catch (error) {
        // Error handling - topic will reappear on next query refresh
        console.error('Failed to delete topic:', error);
      } finally {
        setDeletingTopicId(null);
      }
    },
    [onDelete, removeOptimisticTopic]
  );

  const handleOpenManualDialog = useCallback(() => {
    setIsManualTopicDialogOpen(true);
  }, []);

  const handleCloseManualDialog = useCallback(() => {
    setIsManualTopicDialogOpen(false);
    setEditingTopic(undefined);
  }, []);

  const handleManualTopicSuccess = useCallback(() => {
    // Topics list will auto-refresh via Convex reactivity
    setIsManualTopicDialogOpen(false);
    setEditingTopic(undefined);
  }, []);

  const handleEdit = useCallback((topic: ITopic) => {
    setEditingTopic(topic);
    setIsManualTopicDialogOpen(true);
  }, []);

  const hasTopics = optimisticTopics.length > 0;

  // Sort topics by generation date (newest first)
  const sortedTopics = useMemo(() => {
    return [...optimisticTopics].sort((a, b) => b.generatedAt - a.generatedAt);
  }, [optimisticTopics]);

  // Loading state
  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center space-y-2">
          <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-current border-r-transparent align-[-0.125em] motion-reduce:animate-[spin_1.5s_linear_infinite]" />
          <p className="text-sm text-muted-foreground">Loading topics...</p>
        </div>
      </div>
    );
  }

  // Empty state
  if (!hasTopics) {
    return (
      <div className="flex flex-col items-center justify-center py-12 px-4">
        <div className="text-center space-y-4 max-w-md">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-primary/10">
            <Sparkles className="h-8 w-8 text-primary" />
          </div>
          <div className="space-y-2">
            <h3 className="text-xl font-semibold">No Topics Yet</h3>
            <p className="text-muted-foreground">
              Generate AI-powered content topics to get started. Topics help you create engaging
              content for your audience.
            </p>
          </div>
          <Button
            className="mt-4"
            onClick={() => {
              // This will be connected to open the TopicGenerationModal
              const event = new CustomEvent('openTopicGenerationModal');
              window.dispatchEvent(event);
            }}
          >
            <Sparkles className="h-4 w-4 mr-2" />
            Generate Topics
          </Button>
        </div>
      </div>
    );
  }

  // Topics grid
  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="space-y-1">
          <h2 className="text-[28px] font-bold leading-tight">Your Topics</h2>
          <p className="text-xs font-semibold text-topic-count">
            {sortedTopics.length} {sortedTopics.length === 1 ? 'topic' : 'topics'}
          </p>
        </div>
        <div className="flex items-center gap-3">
          <Button
            onClick={handleOpenManualDialog}
            variant="outline"
            className="rounded-xl gap-2 h-10 px-6 border-foreground-muted/40 text-foreground dark:bg-transparent dark:border-foreground-muted/40 dark:hover:bg-sidebar-accent shadow-none"
          >
            <Plus className="h-4 w-4" />
            Add topic
          </Button>
          <Button
            onClick={() => {
              const event = new CustomEvent('openTopicGenerationModal');
              window.dispatchEvent(event);
            }}
            className="rounded-xl gap-2 h-10 px-4"
          >
            <Sparkles className="h-4 w-4" />
            Generate More
          </Button>
        </div>
      </div>

      <div className="flex flex-col gap-4">
        {sortedTopics.map(topic => (
          <TopicCard
            key={topic._id}
            topic={topic}
            onDelete={handleDelete}
            onGenerate={onGenerate}
            onEdit={handleEdit}
          />
        ))}
      </div>

      {deletingTopicId && (
        <div className="fixed bottom-4 right-4 bg-background border rounded-lg shadow-lg p-4">
          <p className="text-sm">Deleting topic...</p>
        </div>
      )}

      {/* Manual Topic Creation/Edit Dialog */}
      <ManualTopicDialog
        isOpen={isManualTopicDialogOpen}
        onClose={handleCloseManualDialog}
        onSuccess={handleManualTopicSuccess}
        mode={editingTopic ? 'edit' : 'create'}
        initialTopic={editingTopic}
      />
    </div>
  );
};

export { TopicsList };
